import React, { useContext, useEffect, useState } from "react";
import {
  Table,
  Drawer,
  TablePaginationConfig,
  Button,
  Space,
  Input,
  Tooltip,
  Badge,
} from "antd";
import { PageHeader } from "../../../components/page-header/page-header";
import useFetch from "../../../hooks/useFetch";
import { GetCustomerUploadDetailList } from "../../../utils/api.constant";
import {
  ePrivileges,
  eResultCode,
  eWARRANTY_STATUS,
  eOUTPUT_STATUS,
  eEXCEL_STATUS,
} from "../../../utils/enum";
import useToast from "../../../hooks/useToast";
import { ToastType, ToastOpen } from "../../../state/toast/slice";

import { TFileManagerDetail } from "../../../types/index";
import { useNavigate } from "react-router-dom";
import Utils from "../../../utils";
import { UserContext } from "../../../context";
import ActionCard from "../../../components/actionCard/actionCard";
import { TContext, TFilterModel, TPrivilege } from "../../../types/config";
import GenericPopover from "../../../components/pop-over";
import {
  CloseCircleFilled,
  EditFilled,
  FilterOutlined,
  IdcardFilled,
  MoreOutlined,
  OrderedListOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import utils from "../../../utils";
import ColumnManager from "../../../components/configurable-columns";
import DateUtils from "../../../utils/date";
import { defaultFilterParams } from "../../../utils/constants";
import {
  saveColumnPreferences,
  loadColumnPreferences,
  clearColumnPreferences,
} from "../../../utils/column-management";
import ExportToCSV from "../../../components/exporttocsv/ExportToCsv";
import { useColumnSearch } from "../../../components/table/column-search";

const FILE_MANAGER_DETAILS_LIST_COLUMN_KEY = "fileManagerDetailsList";

type TProps = {
  id: number;
  onCloseDrawer: () => void;
};

export default function FileManagerDetailList(props: TProps) {
  const { post } = useFetch();
  const { onShowToast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<TFileManagerDetail[]>([]);
  const context: TContext = useContext(UserContext);
  const [filterParams, setFilterParams] =
    useState<TFilterModel>(defaultFilterParams);
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});
  const { getColumnSearchProps } = useColumnSearch<TFileManagerDetail>();
  const isValidDate = (dateString: string) => {
    if (!dateString || dateString.startsWith("0001-01-01")) {
      return false;
    }
    return !isNaN(new Date(dateString).getTime());
  };

  const initialColumns: any[] = [
    {
      title: "Record ID",
      dataIndex: "recordId",
      key: "recordId",
      width: 150,
      sorter: (a: TFileManagerDetail, b: TFileManagerDetail) =>
        a.recordId.localeCompare(b.recordId),
      ...getColumnSearchProps("recordId"),
      filterSearch: true,
    },
    {
      title: "PO Org",
      dataIndex: "poOrg",
      key: "poOrg",
      width: 120,
      sorter: (a: TFileManagerDetail, b: TFileManagerDetail) =>
        a.poOrg.localeCompare(b.poOrg),
      ...getColumnSearchProps("poOrg"),
      filterSearch: true,
    },
    {
      title: "Part Number",
      dataIndex: "partNumber",
      key: "partNumber",
      width: 150,
      sorter: (a: TFileManagerDetail, b: TFileManagerDetail) =>
        a.partNumber.localeCompare(b.partNumber),
      // filters: utils.getUniqueFilters(data, "partNumber"),
      ...getColumnSearchProps("partNumber"),
      filterSearch: true,
    },
    {
      title: "Part PO Cost",
      dataIndex: "partPOCost",
      key: "partPOCost",
      width: 150,
      sorter: (a: TFileManagerDetail, b: TFileManagerDetail) =>
        parseFloat(a.partPOCost) - parseFloat(b.partPOCost),
    },
    {
      title: "Unit Serial Number",
      dataIndex: "serialNumber",
      key: "serialNumber",
      width: 180,
      sorter: (a: TFileManagerDetail, b: TFileManagerDetail) =>
        a.serialNumber.localeCompare(b.serialNumber),
      dynamicFilters: true,
      ...getColumnSearchProps("serialNumber"),
      filterSearch: true,
    },
    {
      title: "Debrief Date",
      dataIndex: "dbriefDate",
      key: "dbriefDate",
      width: 150,
      sorter: (a: TFileManagerDetail, b: TFileManagerDetail) => {
        const dateA = isValidDate(a.dbriefDate)
          ? new Date(a.dbriefDate).getTime()
          : 0;
        const dateB = isValidDate(b.dbriefDate)
          ? new Date(b.dbriefDate).getTime()
          : 0;
        return dateA - dateB;
      },
      render: (text: string) =>
        isValidDate(text)
          ? DateUtils.format(text, context?.user?.dateFormat)
          : "-",
    },
    {
      title: "SA Subject",
      dataIndex: "saSubject",
      key: "saSubject",
      width: 300,
      sorter: (a: TFileManagerDetail, b: TFileManagerDetail) =>
        a.saSubject.localeCompare(b.saSubject),
      ...getColumnSearchProps("saSubject"),
      filterSearch: true,
    },
    {
      title: "Ship Tracking Number",
      dataIndex: "shipTrackingNumber",
      key: "shipTrackingNumber",
      width: 200,
      sorter: (a: TFileManagerDetail, b: TFileManagerDetail) =>
        a.shipTrackingNumber.localeCompare(b.shipTrackingNumber),
      ...getColumnSearchProps("shipTrackingNumber"),
      filterSearch: true,
    },
    {
      title: "FedEx Date",
      dataIndex: "fedexDate",
      key: "fedexDate",
      width: 150,
      sorter: (a: TFileManagerDetail, b: TFileManagerDetail) => {
        const dateA = isValidDate(a.fedexDate)
          ? new Date(a.fedexDate).getTime()
          : 0;
        const dateB = isValidDate(b.fedexDate)
          ? new Date(b.fedexDate).getTime()
          : 0;
        return dateA - dateB;
      },
      render: (text: string) =>
        isValidDate(text)
          ? DateUtils.format(text, context?.user?.dateFormat)
          : "-",
    },
    {
      title: "FedEx Signed",
      dataIndex: "fedexSigned",
      key: "fedexSigned",
      width: 150,
      sorter: (a: TFileManagerDetail, b: TFileManagerDetail) =>
        a.fedexSigned.localeCompare(b.fedexSigned),
    },
    {
      title: "Manufacturing Date",
      dataIndex: "manufacturingDate",
      key: "manufacturingDate",
      width: 200,
      sorter: (a: TFileManagerDetail, b: TFileManagerDetail) => {
        const dateA = isValidDate(a.manufacturingDate)
          ? new Date(a.manufacturingDate).getTime()
          : 0;
        const dateB = isValidDate(b.manufacturingDate)
          ? new Date(b.manufacturingDate).getTime()
          : 0;
        return dateA - dateB;
      },
      render: (text: string) =>
        isValidDate(text)
          ? DateUtils.format(text, context?.user?.dateFormat)
          : "-",
    },
    {
      title: "Warranty Date",
      dataIndex: "warrantyDate",
      key: "warrantyDate",
      width: 180,
      sorter: (a: TFileManagerDetail, b: TFileManagerDetail) => {
        const dateA = isValidDate(a.warrantyDate)
          ? new Date(a.warrantyDate).getTime()
          : 0;
        const dateB = isValidDate(b.warrantyDate)
          ? new Date(b.warrantyDate).getTime()
          : 0;
        return dateA - dateB;
      },
      render: (text: string) =>
        isValidDate(text)
          ? DateUtils.format(text, context?.user?.dateFormat)
          : "-",
    },
    {
      title: "Warranty Status",
      dataIndex: "warrantyStatus",
      key: "warrantyStatus",
      width: 280,
      sorter: (a: TFileManagerDetail, b: TFileManagerDetail) =>
        a.warrantyStatus - b.warrantyStatus,
      filters: [
        { text: "In Warranty", value: eWARRANTY_STATUS.IN_WARRANTY },
        { text: "Out of Warranty", value: eWARRANTY_STATUS.OUT_OF_WARRANTY },
        {
          text: "In Warranty and Claimed",
          value: eWARRANTY_STATUS.IN_WARRANTY_CLAIMED,
        },
        {
          text: "In Warranty and not Claimed",
          value: eWARRANTY_STATUS.IN_WARRANTY_NOT_CLAIMED,
        },
      ],
      onFilter: (
        value: string | number | boolean,
        record: TFileManagerDetail
      ) => record.warrantyStatus === value,
      render: (status: eWARRANTY_STATUS, record: TFileManagerDetail) => {
        const statusText =
          status === eWARRANTY_STATUS.IN_WARRANTY
            ? "In Warranty"
            : status === eWARRANTY_STATUS.IN_WARRANTY_CLAIMED
              ? "In Warranty and Claimed"
              : status === eWARRANTY_STATUS.IN_WARRANTY_NOT_CLAIMED
                ? "In Warranty and Not Claimed"
                : "Out of Warranty";
        const statusColor =
          record.outputStatus === eOUTPUT_STATUS.GREEN ? "success" : "error";
        return <Badge status={statusColor} text={statusText} />;
      },
    },
    {
      title: "Output Status",
      dataIndex: "outputStatus",
      key: "outputStatus",
      width: 150,
      sorter: (a: TFileManagerDetail, b: TFileManagerDetail) =>
        a.outputStatus - b.outputStatus,
      filters: [
        { text: "Green", value: eOUTPUT_STATUS.GREEN },
        { text: "Red", value: eOUTPUT_STATUS.RED },
      ],
      onFilter: (
        value: string | number | boolean,
        record: TFileManagerDetail
      ) => record.outputStatus === value,
      render: (status: eOUTPUT_STATUS) => {
        const statusText = status === eOUTPUT_STATUS.GREEN ? "Green" : "Red";
        const statusColor =
          status === eOUTPUT_STATUS.GREEN ? "success" : "error";
        return <Badge status={statusColor} text={statusText} />;
      },
    },
    {
      title: "Excel Status",
      dataIndex: "excelStatus",
      key: "excelStatus",
      width: 150,
      sorter: (a: TFileManagerDetail, b: TFileManagerDetail) =>
        a.excelStatus - b.excelStatus,
      filters: [
        { text: "Present", value: eEXCEL_STATUS.PRESENT },
        { text: "Not Present", value: eEXCEL_STATUS.NOT_PRESENT },
      ],
      onFilter: (
        value: string | number | boolean,
        record: TFileManagerDetail
      ) => record.excelStatus === value,
      render: (status: eEXCEL_STATUS) => {
        const statusText =
          status === eEXCEL_STATUS.PRESENT ? "Present" : "Not Present";
        const statusColor =
          status === eEXCEL_STATUS.PRESENT ? "success" : "error";
        return <Badge status={statusColor} text={statusText} />;
      },
    },
    {
      title: "Excel Upload Date",
      dataIndex: "excelUploadDate",
      key: "excelUploadDate",
      width: 200,
      sorter: (a: TFileManagerDetail, b: TFileManagerDetail) => {
        const dateA = isValidDate(a.excelUploadDate)
          ? new Date(a.excelUploadDate).getTime()
          : 0;
        const dateB = isValidDate(b.excelUploadDate)
          ? new Date(b.excelUploadDate).getTime()
          : 0;
        return dateA - dateB;
      },
      render: (text: string) =>
        isValidDate(text)
          ? DateUtils.format(text, context?.user?.dateFormat)
          : "-",
    },
    // {
    //   title: "Action",
    //   key: "actions",
    //   align: "center",
    //   fixed: "right",
    //   width: 80,
    //   render: (record: TFileManagerDetail) => (
    //     <GenericPopover
    //       placement="left"
    //       content={
    //         <div className="standardActionBox">
    //           {/* {Utils.isUserHasAccess(
    //             context.user?.privilegeList as TPrivilege[],
    //             ePrivileges.EDITCUSTOMERS
    //           ) ? (
    //             <CustomDrawer
    //               trigger={
    //                 <ActionCard text="Edit">
    //                   <EditFilled />
    //                 </ActionCard>
    //               }
    //               component={BulkUploadForm}
    //               componentProps={{
    //                 id: record.id,
    //                 onRefreshList: fetchData,
    //               }}
    //               title="Edit Record"
    //               size="default"
    //             />
    //           ) : null} */}
    //         </div>
    //       }
    //       icon={
    //         <MoreOutlined style={{ fontSize: "20px", cursor: "pointer" }} />
    //       }
    //     />
    //   ),
    // },
  ];
  const [columns, setColumns] = useState(initialColumns);
  const [draftColumns, setDraftColumns] = useState(columns);
  const [searchText, setSearchText] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(false);

  useEffect(() => {
    (async () => {
      const savedPreferences = await loadColumnPreferences(
        FILE_MANAGER_DETAILS_LIST_COLUMN_KEY
      );
      if (savedPreferences) {
        const initialKeys = new Set(initialColumns.map((c) => c.key));
        const savedKeys = new Set(savedPreferences.map((p: any) => p.key));

        if (
          initialKeys.size !== savedKeys.size ||
          !Array.from(initialKeys).every((key) => savedKeys.has(key))
        ) {
          clearColumnPreferences(FILE_MANAGER_DETAILS_LIST_COLUMN_KEY);
          setColumns(initialColumns);
          setDraftColumns(initialColumns);
          return;
        }

        const initialColumnsMap = new Map(
          initialColumns.map((col) => [col.key, col])
        );
        const reorderedColumns = savedPreferences
          .map((pref: any) => {
            const col = initialColumnsMap.get(pref.key);
            if (col) {
              return { ...col, hidden: pref.hidden };
            }
            return null;
          })
          .filter((col: any) => col !== null);

        setColumns(reorderedColumns as any[]);
        setDraftColumns(reorderedColumns as any[]);
      }
    })();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const requestPayload = {
        data: {
          currentPage: 1,
          pageSize: -1,
          customerUploadMasterId: props.id,
        },
      };

      const response = await post(GetCustomerUploadDetailList, requestPayload);
      const { dataResponse, data, filterModel } = response;

      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        const formattedData = data.map(
          (item: TFileManagerDetail, index: number) => ({
            ...item,
            key: (item.id ?? "").toString(),
            srNo: index + 1,
          })
        );
        setData(formattedData as TFileManagerDetail[]);
        setFilterParams({ ...(filterModel as TFilterModel) });
        setIsLoading(false);
        // updateColumnFilters(formattedData);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: dataResponse.description,
        });
        setIsLoading(false);
      }
    } catch (error) {
      onShowToast({
        type: ToastType.error,
        title: <CloseCircleFilled />,
        position: ToastOpen.leftBottom,
        content: "Failed to fetch file data",
      });
      setIsLoading(false);
    }
  };

  const updateColumnFilters = (dataset: TFileManagerDetail[]) => {
        const getFilters = (key: keyof TFileManagerDetail) => {
          const uniqueValues = Array.from(
            new Set(
              dataset
                ?.map((item) => item?.[key])
                ?.filter(
                  (val) =>
                    val !== null && val !== undefined && val !== "" && val !== "-"
                ) as string[]
            )
          );
    
          return uniqueValues.map((value) => ({ text: value, value }));
        };
    const newColumns = columns.map((col) => {
      if (col.dynamicFilters) {
        return {
          ...col,
          filters: getFilters(col.dataIndex as keyof TFileManagerDetail),
        };
      }
      return col;
    });
    setColumns(newColumns);
  };

  useEffect(() => {
    if (props.id) {
      fetchData();
    }
  }, [props.id]);

  useEffect(() => {
    if (data.length > 0) {
      updateColumnFilters(data);
    }
  }, [data]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const filteredData = data.filter((item) => {
    if (!searchText) return true;
    return Object.values(item).some(
      (val) =>
        val && val.toString().toLowerCase().includes(searchText.toLowerCase())
    );
  });

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter: any
  ) => {
    setActiveFilters(filters);
    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if ("dataIndex" in col) {
          return {
            ...col,
            filteredValue: filters[col.dataIndex as string] || null,
            sortOrder: col.dataIndex === sorter.field ? sorter.order : null,
          };
        }
        return col;
      })
    );
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    setDraftColumns((prevDraftColumns) => {
      const reorderedColumns = Array.from(prevDraftColumns);
      const [removed] = reorderedColumns.splice(result.source.index, 1);
      reorderedColumns.splice(result.destination.index, 0, removed);
      return reorderedColumns;
    });
  };

  const toggleColumn = (key: string) => {
    setDraftColumns((prevDraftColumns) =>
      prevDraftColumns.map((col) =>
        col.key === key ? { ...col, hidden: !col.hidden } : col
      )
    );
  };

  const handleResetColumns = () => {
    setDraftColumns(initialColumns);
    clearColumnPreferences(FILE_MANAGER_DETAILS_LIST_COLUMN_KEY);
  };

  const handleSaveColumns = () => {
    setColumns(draftColumns);
    saveColumnPreferences(FILE_MANAGER_DETAILS_LIST_COLUMN_KEY, draftColumns);
    setDrawerVisible(false);
  };

  const visibleColumns = columns?.filter((col) => !col?.hidden);

  const handleResetAll = () => {
    setActiveFilters({});
    setSearchText("");
    setColumns((prevColumns) =>
      prevColumns.map((col) => ({
        ...col,
        filteredValue: null,
        sortOrder: null,
      }))
    );
  };

  const exportHeaders = columns
    ?.filter(
      (column) =>
        column?.key && (column?.title as string)?.toLowerCase() !== "action"
    )
    .map((column) => ({
      label: column?.title,
      key: column?.key,
    }));

  const exportData = data.map((user) => ({
    ...user,
  }));

  return (
    <div className="pageBody">
      <PageHeader
        icon={<IdcardFilled />}
        title={`${filterParams.totalRows} Total Details`}
        size="large"
      >
        <Space>
          <Button
            type="default"
            loading={isLoading}
            disabled={isLoading}
            onClick={props.onCloseDrawer}
          >
            Close
          </Button>
          <Input
            placeholder="Filter by keywords"
            value={searchText}
            onChange={handleSearch}
            style={{ width: 200, borderColor: "var(--bs-primary-light)" }}
            prefix={<SearchOutlined />}
          />
          <ExportToCSV
            data={exportData}
            headers={exportHeaders}
            fileName={`details_${DateUtils.format(new Date())}.csv`}
          />
          <Tooltip title="Reset Filters">
            <FilterOutlined onClick={handleResetAll} />
          </Tooltip>
          <Tooltip title="Configure Columns">
            <OrderedListOutlined
              onClick={() => {
                setDraftColumns(columns);
                setDrawerVisible(true);
              }}
            />
          </Tooltip>
        </Space>
      </PageHeader>

      <div className="tableWrap">
        <Table
          columns={visibleColumns}
          dataSource={filteredData}
          loading={isLoading}
          pagination={{
            position: ["bottomLeft"],
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total, range) =>
              `Showing ${range[0]}-${range[1]} of ${total} total entries`,
          }}
          onChange={handleTableChange}
          bordered={false}
          size="small"
          sticky
          scroll={{
            y: "calc(100vh - 310px)",
            x: "max-content",
          }}
        />
      </div>

      <Drawer
        title="Customize Columns"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
      >
        <ColumnManager
          columns={draftColumns}
          initialColumns={initialColumns}
          onDragEnd={handleDragEnd}
          onToggleColumn={toggleColumn}
          onResetColumns={handleResetColumns}
          onSave={handleSaveColumns}
          onCancel={() => setDrawerVisible(false)}
        />
      </Drawer>
    </div>
  );
}
